import React, { useEffect, useRef, useState } from "react";
import AdminLayout from "../../components/AdminLayout";
import QRScannerModal from "../../components/QRScannerModal";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Plus, Trash2, X, QrCode, Upload, AlertTriangle, ScanLine } from "lucide-react";
import { toast } from "sonner";

export default function QRCodesPage() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "superadmin";
    const [codes, setCodes] = useState([]);
    const [status, setStatus] = useState("all");
    const [search, setSearch] = useState("");
    const [addOpen, setAddOpen] = useState(false);
    const [newCode, setNewCode] = useState("");
    const [importOpen, setImportOpen] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [scannerOpen, setScannerOpen] = useState(false);
    const fileRef = useRef(null);

    const load = async () => {
        const params = {};
        if (status !== "all") params.status = status;
        if (search) params.search = search;
        const { data } = await api.get("/qr-pool", { params });
        setCodes(data);
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, search]);

    const addCode = async (e) => {
        e.preventDefault();
        try {
            await api.post("/qr-pool", { code: newCode });
            toast.success("Code added");
            setNewCode("");
            setAddOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Failed to add code");
        }
    };

    const removeCode = async (c) => {
        if (!window.confirm(`Delete QR code ${c.code}?`)) return;
        try {
            await api.delete(`/qr-pool/${c.id}`);
            toast.success("Code deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
        }
    };

    const markLost = async (c) => {
        if (!window.confirm(`Report ${c.code} as lost? It can never be allotted again.`)) return;
        try {
            await api.post(`/qr-pool/${c.id}/mark-lost`);
            toast.success(`${c.code} marked lost`);
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not mark lost");
        }
    };

    const runImport = async (e) => {
        e.preventDefault();
        const file = fileRef.current?.files?.[0];
        if (!file) {
            toast.error("Choose a file first");
            return;
        }
        setImporting(true);
        setImportResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            // Do NOT set Content-Type manually — axios needs to generate its own
            // multipart boundary, which a hardcoded header value strips out.
            const { data } = await api.post("/qr-pool/import", fd);
            setImportResult(data);
            toast.success(`Imported ${data.added} new codes`);
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
        } finally {
            setImporting(false);
        }
    };

    const available = codes.filter((c) => c.status === "available").length;
    const assigned = codes.filter((c) => c.status === "assigned").length;
    const lost = codes.filter((c) => c.status === "lost").length;

    return (
        <AdminLayout>
            <div className="p-4 sm:p-8 lg:p-12 max-w-[1400px]">
                <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                    <div>
                        <p className="label-caps mb-2">Allotment</p>
                        <h1 className="font-display text-5xl text-[#111111]">QR Codes</h1>
                        <p className="text-[#666666] mt-2">
                            {codes.length} in pool · {available} available · {assigned} assigned · {lost} lost
                        </p>
                    </div>
                    {isSuperAdmin ? (
                        <div className="flex gap-3">
                            <button
                                onClick={() => setAddOpen(true)}
                                data-testid="add-code-button"
                                className="bg-white text-[#111111] border border-[#111111] font-bold px-5 py-3 hover:bg-[#F8F9FA] flex items-center gap-2 transition-colors"
                            >
                                <Plus size={18} /> Add Code
                            </button>
                            <button
                                onClick={() => {
                                    setImportResult(null);
                                    setImportOpen(true);
                                }}
                                data-testid="import-codes-button"
                                className="bg-[#FF3B30] text-white font-bold px-5 py-3 hover:bg-[#D70015] flex items-center gap-2 transition-colors"
                            >
                                <Upload size={18} /> Import Codes
                            </button>
                        </div>
                    ) : (
                        <div
                            className="flex items-center gap-2 text-sm text-[#666666] border border-[#E5E5E5] bg-[#F8F9FA] px-4 py-3"
                            data-testid="superadmin-only-note"
                        >
                            <AlertTriangle size={16} />
                            Only Super Admin can add or import QR codes.
                        </div>
                    )}
                </div>

                <div className="flex gap-3 mb-6 flex-wrap">
                    {[
                        { k: "all", label: "All" },
                        { k: "available", label: "Available" },
                        { k: "assigned", label: "Assigned" },
                        { k: "lost", label: "Lost" },
                    ].map((f) => (
                        <button
                            key={f.k}
                            onClick={() => setStatus(f.k)}
                            data-testid={`filter-${f.k}`}
                            className={`px-4 py-2 text-sm font-bold border transition-colors ${
                                status === f.k
                                    ? "bg-[#111111] text-white border-[#111111]"
                                    : "bg-white text-[#111111] border-[#E5E5E5] hover:border-[#111111]"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                    <input
                        type="text"
                        placeholder="Search by code…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="code-search"
                        className="flex-1 min-w-[220px] bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-2 text-sm focus:border-[#111111] outline-none font-mono"
                    />
                </div>

                <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F8F9FA] border-b-2 border-[#111111]">
                            <tr>
                                <th className="text-left font-bold py-3 px-4">Code</th>
                                <th className="text-left font-bold py-3 px-4">Status</th>
                                <th className="text-left font-bold py-3 px-4">Assigned To</th>
                                <th className="text-right font-bold py-3 px-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody data-testid="qr-pool-table">
                            {codes.map((c) => (
                                <tr key={c.id} className="border-b border-[#E5E5E5] hover:bg-[#F8F9FA]">
                                    <td className="py-3 px-4 font-mono text-xs">{c.code}</td>
                                    <td className="py-3 px-4">
                                        <span
                                            className={`font-bold px-2 py-1 text-xs uppercase tracking-wider border ${
                                                c.status === "available"
                                                    ? "bg-[#34C759]/10 text-[#34C759] border-[#34C759]/30"
                                                    : c.status === "lost"
                                                    ? "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/30"
                                                    : "bg-[#111111]/5 text-[#111111] border-[#111111]/20"
                                            }`}
                                        >
                                            {c.status}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-[#666666]">
                                        {c.assigned_student_name
                                            ? `${c.assigned_student_name} (${c.assigned_student_roll})`
                                            : "—"}
                                    </td>
                                    <td className="py-3 px-4">
                                        <div className="flex gap-1 justify-end">
                                            <button
                                                onClick={() => markLost(c)}
                                                disabled={c.status === "lost"}
                                                data-testid={`mark-lost-${c.code}`}
                                                title={c.status === "lost" ? "Already lost" : "Report Lost"}
                                                className="p-2 hover:bg-[#111111] hover:text-white transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-inherit"
                                            >
                                                <AlertTriangle size={16} />
                                            </button>
                                            <button
                                                onClick={() => removeCode(c)}
                                                disabled={c.status === "assigned"}
                                                data-testid={`delete-code-${c.code}`}
                                                title={c.status === "assigned" ? "Unassign from student first" : "Delete"}
                                                className="p-2 hover:bg-[#FF3B30] hover:text-white transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-inherit"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {codes.length === 0 && (
                                <tr>
                                    <td colSpan="4" className="p-8 text-center text-[#666666]">
                                        <QrCode className="mx-auto mb-2" size={24} />
                                        No QR codes in the pool yet. Import the list you distributed to the university.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {addOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white border-2 border-[#111111] w-full max-w-sm animate-slide-up">
                        <div className="flex justify-between items-center px-6 py-4 border-b-2 border-[#111111]">
                            <h3 className="font-display text-2xl">Add Code</h3>
                            <button onClick={() => setAddOpen(false)} className="p-1 hover:bg-[#F8F9FA]">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={addCode} className="p-6 space-y-4">
                            <div>
                                <label className="label-caps block mb-2">QR Code</label>
                                <div className="flex gap-2">
                                    <input
                                        required
                                        data-testid="new-code-input"
                                        value={newCode}
                                        onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                                        className="flex-1 bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-2 focus:border-[#111111] outline-none font-mono uppercase"
                                        placeholder="SCX-0042"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setScannerOpen(true)}
                                        data-testid="scan-new-code-button"
                                        title="Scan instead of typing"
                                        className="px-3 border border-[#111111] hover:bg-[#111111] hover:text-white transition-colors"
                                    >
                                        <ScanLine size={18} />
                                    </button>
                                </div>
                                <p className="text-xs text-[#666666] mt-1">
                                    Cards only show the QR artwork — scan it rather than typing what you can't read.
                                </p>
                            </div>
                            <button
                                type="submit"
                                data-testid="add-code-submit"
                                className="w-full bg-[#FF3B30] text-white font-bold py-3 hover:bg-[#D70015]"
                            >
                                Add to Pool
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {importOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white border-2 border-[#111111] w-full max-w-md animate-slide-up">
                        <div className="flex justify-between items-center px-6 py-4 border-b-2 border-[#111111]">
                            <h3 className="font-display text-2xl">Import Codes</h3>
                            <button onClick={() => setImportOpen(false)} className="p-1 hover:bg-[#F8F9FA]">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={runImport} className="p-6 space-y-4">
                            <p className="text-sm text-[#666666]">
                                Upload an Excel (.xlsx) or CSV file with one column of the codes already printed and
                                distributed. Duplicates are skipped automatically.
                            </p>
                            <input
                                type="file"
                                ref={fileRef}
                                accept=".xlsx,.xls,.csv"
                                data-testid="import-codes-file"
                                className="w-full bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-2 text-sm"
                            />
                            {importResult && (
                                <div className="bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-3 text-sm">
                                    <p>
                                        <span className="font-bold text-[#34C759]">{importResult.added}</span> added ·{" "}
                                        <span className="font-bold text-[#666666]">{importResult.duplicates}</span> duplicates ·{" "}
                                        <span className="font-bold text-[#FF3B30]">{importResult.invalid}</span> invalid
                                    </p>
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={importing}
                                data-testid="import-codes-submit"
                                className="w-full bg-[#FF3B30] text-white font-bold py-3 hover:bg-[#D70015] disabled:opacity-60"
                            >
                                {importing ? "Importing…" : "Import"}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {scannerOpen && (
                <QRScannerModal
                    title="Scan QR Card"
                    onDetected={(code) => {
                        setNewCode(code.trim().toUpperCase());
                        setScannerOpen(false);
                    }}
                    onClose={() => setScannerOpen(false)}
                />
            )}
        </AdminLayout>
    );
}
